import { CardTypes } from '@/enums/payment-types.enum';
import {
  IPaymentSecurityFooterData,
  IPaymentSecurityFooterOption,
  IPaymentSecurityFooterOptions,
  ISitecorePaymentSecurityFooterPaymentType,
} from '@/interfaces/forms/payment-security-footer.interface';

const CLASS_NAME_BY_CARD_TYPE: Record<CardTypes, string> = {
  [CardTypes.Visa]: 'payment-icon-visa',
  [CardTypes.MasterCard]: 'payment-icon-mastercard',
  [CardTypes.Amex]: 'payment-icon-amex',
  [CardTypes.GooglePay]: 'payment-icon-google-pay',
  [CardTypes.ApplePay]: 'payment-icon-apple-pay',
};

const LABEL_BY_CARD_TYPE: Record<CardTypes, string> = {
  [CardTypes.Visa]: 'Visa',
  [CardTypes.MasterCard]: 'Mastercard',
  [CardTypes.Amex]: 'American Express',
  [CardTypes.GooglePay]: 'Google Pay',
  [CardTypes.ApplePay]: 'Apple Pay',
};

// Sitecore item names aren't guaranteed unique (e.g. "Mastercard1"), so match on a leading card type instead
function resolveCardType(name?: string): CardTypes | undefined {
  const normalisedName = (name ?? '').toLowerCase().replace(/[^a-z]/g, '');
  return (Object.values(CardTypes) as CardTypes[]).find((cardType) => normalisedName.startsWith(cardType));
}

function toPaymentOption(item: ISitecorePaymentSecurityFooterPaymentType): IPaymentSecurityFooterOption | undefined {
  const cardType = resolveCardType(item.name ?? item.displayName);
  const src = item.fields?.Image?.value?.src;

  if (!cardType || !src) {
    return undefined;
  }

  return {
    type: cardType,
    src,
    label: item.fields?.Image?.value?.alt || LABEL_BY_CARD_TYPE[cardType],
    className: CLASS_NAME_BY_CARD_TYPE[cardType],
  };
}

export function normalisePaymentOptions(data?: IPaymentSecurityFooterData): IPaymentSecurityFooterOptions | null {
  if (!data?.paymentSecurityFooterEnabled?.value) {
    return null;
  }

  const seenCardTypes = new Set<CardTypes>();
  const options = (data?.paymentSecurityFooterPaymentTypes ?? []).reduce<IPaymentSecurityFooterOption[]>(
    (configuredOptions, item) => {
      const option = toPaymentOption(item);

      if (option && !seenCardTypes.has(option.type)) {
        seenCardTypes.add(option.type);
        configuredOptions.push(option);
      }

      return configuredOptions;
    },
    []
  );

  return {
    title: data?.paymentSecurityFooterTitle,
    body: data?.paymentSecurityFooterBody,
    options,
  };
}
